import { ButtonBase, CircularProgress, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { useCallback, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { CreateProjectModal } from "../components/CreateProjectModal";
import { ProjectCard } from "../components/ProjectCard";
import { ICertificateDTO, IProject } from "../entities/projects";
import { AppDispatch, IState } from "../lib/store";
import { createProject, deleteDiplomaFolder } from "../lib/store/projects/thunks";
import { NewProject } from "../lib/toast/NewProject";
import ConfirmationDialog from "../components/ConfirmationDialog";
import { ICertificate } from "../entities/certificates";
import { certificatesTableDataMapper } from "../lib/helper";
import { downloadCertificates } from "../lib/download";

const geToData = (path: string[], data: Record<string, any>) => {
    if (path.length === 0) {
        return data;
    }

    let d = data;

    path.map((p: any) => {
        d = d[p];
    });

    return d;
}

// temporary function to get the institution from the certificates
const getInstitution = (c: ICertificateDTO): string => {
    if (!c || Object.keys(c).length === 0) return '';
    let data: any;
    const year = Object.keys(c)[0];
    data = c[year];
    const grade = Object.keys(data)[0];
    data = data[grade];
    const program = Object.keys(data)[0];
    data = data[program];
    return data[0]["metadata"]["institution"]
}

function flattenCertificates(certs: { [grade: string]: { [program: string]: ICertificate[] } }): ICertificate[] {
    const result: ICertificate[] = [];
    for (const grade of Object.keys(certs)) {
        const programs = certs[grade];
        for (const program of Object.keys(programs)) {
            const certificates = programs[program];
            result.push(...certificates);
        }
      
    }
    return result;
  }

const excelHeader = [
    "Institution",
    "Prénom",
    "Nom",
    "Date de naissance",
    "INE",
    "Matricule",
    "Diplôme",
    "Programme d\'étude",
    "Année d\'obtention",
];

export const Project: React.FC = ({}) => {
    const [openModal, setOpenModal] = useState<boolean>();
    const loading = useSelector((state: IState) => state.projects.loading, shallowEqual);
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate()
    const matches = useMediaQuery('(min-width:600px)');
    const params = useParams();
    const path = [params['year'], params['grade'], params['program']].filter((p) => !!p) as string[];
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteFilter, setDeleteFilter] = useState<{year: string, grade: string | undefined, program: string | undefined}>();
    const certificates = useSelector((state: IState) => state.projects.certificates, shallowEqual);


    const handleClose = () => {
        setOpenModal(false);
    };

    const onSubmit = (project: IProject, reset: () => void) => {
        dispatch(createProject(project))
            .then(() => {
                toast.success(<NewProject name={project.name!} />);
                setOpenModal(false);
                reset();
            })
    }

    const deleteAction = useCallback(() => {
        setDeleteLoading(true);
        dispatch(deleteDiplomaFolder({...(deleteFilter!), institution: getInstitution(certificates), hash: undefined}))
            .then(() =>  toast.success('dossier supprimé avec succès'))
            .catch(() =>  toast.error('Une erreur est survenue ... veuillez contacter le support si l\'erreur persiste.'))
            .finally(() => {
                setDeleteLoading(false);
                setOpenDeleteModal(false);
            })
        }
    , [deleteFilter]);

    const onDeleteFolder = (level: string) => {
        path.push(level);
        setDeleteFilter({
            year: path[0],
            grade: path.length >= 2 ? path[1] : undefined,
            program: path.length >= 3 ? path[2] : undefined
        })

        setOpenDeleteModal(true);
    }

    const downloadAction = (level: string) => {
        if (path.length === 0) {
            const certs = flattenCertificates(certificates[level]!);
            downloadCertificates(
                excelHeader,
                certificatesTableDataMapper(certs).map((c) => ({
                    institution: c.institution,
                    firstname: c.firstname,
                    lastname: c.lastname,
                    birthDate: c.birthDate,       
                    ine: c.ine,
                    matricule: c.matricule,
                    grade: c.grade,
                    program: c.program,
                    year: c.annee_obtention,
                }))
            , `Diplômes_${certs[0].metadata?.institution}_${level}`);
        }
    }
    
    return (
        <>
            {
                loading ?
                (   
                    <Box textAlign="center"><CircularProgress sx={{color: '#005e7a'}} /></Box>
                ):
                (   
                    <>
                        <Divider sx={{ margin: '2rem 0'}}/>
                        <Box width="100%" display="flex">
                            <Box display="flex" flexWrap="wrap" justifyContent={matches ? undefined : 'center'} gap={5}>
                                {!!path && !!certificates &&  Object.keys(geToData(path, certificates)).map((level: string, idx: number) => {
                                    switch(path.length) {
                                        case 1:
                                            return (
                                                <ButtonBase key={idx} onClick={() =>  navigate(level)}>
                                                    <ProjectCard variant="folder" onDeleteFolder={() => onDeleteFolder(level)} label={
                                                        <div className="color-chart" style={{fontSize: '15px', display: 'flex', flexDirection: 'column'}}>
                                                            <strong>Diplôme: </strong>
                                                            <span style={{color: level.toLowerCase().includes('indefinie') ? 'red' : ''}}>
                                                                {level}
                                                            </span>
                                                        </div>
                                                    }/>
                                                </ButtonBase>
                                            )

                                        case 2:
                                            return (
                                                <ButtonBase key={idx} onClick={() => navigate(level)}>
                                                    <ProjectCard variant="folder" onDeleteFolder={() => onDeleteFolder(level)} label={
                                                        <span className="color-chart" style={{fontSize: '15px', display: 'flex', flexDirection: 'column'}}>
                                                            <strong>Progamme: </strong>
                                                            <span style={{color: level.toLowerCase().includes('indefinie') ? 'red' : ''}}>
                                                                {level}
                                                            </span>
                                                        </span>
                                                    }/>
                                                </ButtonBase>
                                            )
                                            
                                        default:
                                            return (
                                                <ButtonBase key={idx} onClick={() => navigate(level)}>
                                                    <ProjectCard variant="folder" onDownload={() => downloadAction(level)} onDeleteFolder={() => onDeleteFolder(level)} label={
                                                            <span className="color-chart" style={{fontSize: '15px'}}>
                                                                <strong>Année: </strong>
                                                                <span style={{color: level.toLowerCase().includes('indefinie') ? 'red' : ''}}>
                                                                    {level}
                                                                </span>
                                                            </span>
                                                        }/>
                                                </ButtonBase>    
                                            )
                                    }
                                    })}
                            </Box>
                        </Box>
                    </>
                )
            }

            <CreateProjectModal open={!!openModal} loading={!!loading} handleCancel={handleClose} onSubmit={onSubmit}/>
            <ConfirmationDialog actionLabel="Suppression" loading={deleteLoading} open={openDeleteModal} close={() => setOpenDeleteModal(false)} action={deleteAction}/>
        </>
    )
}
